import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Layout, Search, Plus, Copy, Check, X, Trash2, Layers, Building2 } from 'lucide-react';
import { RelationChips, RelationChip } from './RelationChips';
import { useIntegrationGraph } from '../../../hooks/useIntegrationGraph';

interface SaasAccount {
  id: string;
  email: string;
  company_name: string;
  website: string;
  contact_email: string;
  contact_phone: string;
  city: string;
  created_at: string;
  status: 'active' | 'inactive';
  total_integrations: number;
}

interface SaasInvitation {
  id: string;
  token: string;
  invited_company_name: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  used_by: string | null;
  used_email: string | null;
}

interface HotelOption {
  id: string;
  company_name: string | null;
  email: string;
  city?: string | null;
  country?: string | null;
}

interface ConnectedHotelRow {
  integration_id: string;
  hotel_id: string;
  company_name: string | null;
  hotel_code: string | null;
  email: string;
  scopes: string[];
  status: string;
}

interface SaasAccountsProps {
  /** Search term pushed in when arriving from another listing's relation chip. */
  initialSearch?: string | null;
  onInitialSearchConsumed?: () => void;
  onOpenHotel?: (hotelName: string) => void;
}

export function SaasAccounts({
  initialSearch,
  onInitialSearchConsumed,
  onOpenHotel,
}: SaasAccountsProps = {}) {
  const [accounts, setAccounts] = useState<SaasAccount[]>([]);
  const {
    graph,
    emptyLabel: relationEmptyLabel,
    emptyVariant: relationEmptyVariant,
  } = useIntegrationGraph();
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [generatedInvitationUrl, setGeneratedInvitationUrl] = useState<string | null>(null);
  const [generatingInvitation, setGeneratingInvitation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invitations, setInvitations] = useState<SaasInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitedCompanyName, setInvitedCompanyName] = useState('');
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null);
  const [deletingInvitationId, setDeletingInvitationId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<SaasAccount | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [accountScopesModal, setAccountScopesModal] = useState<SaasAccount | null>(null);
  const [availableScopes, setAvailableScopes] = useState<{ code: string; name: string }[]>([]);
  const [selectedScopeCodes, setSelectedScopeCodes] = useState<string[]>([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [savingScopes, setSavingScopes] = useState(false);
  const [scopesModalError, setScopesModalError] = useState('');
  const [accountAddHotelModal, setAccountAddHotelModal] = useState<SaasAccount | null>(null);
  const [addHotelHotels, setAddHotelHotels] = useState<HotelOption[]>([]);
  const [addHotelConnectedIds, setAddHotelConnectedIds] = useState<Set<string>>(new Set());
  const [addHotelSelectedHotelId, setAddHotelSelectedHotelId] = useState<string | null>(null);
  const [addHotelScopes, setAddHotelScopes] = useState<string[]>([]);
  const [addHotelAvailableScopes, setAddHotelAvailableScopes] = useState<{ code: string; name: string }[]>([]);
  const [addHotelLoading, setAddHotelLoading] = useState(false);
  const [addHotelSaving, setAddHotelSaving] = useState(false);
  const [addHotelError, setAddHotelError] = useState('');
  const [addHotelSearchTerm, setAddHotelSearchTerm] = useState('');
  const [accountConnectedHotelsModal, setAccountConnectedHotelsModal] = useState<SaasAccount | null>(null);
  const [connectedHotelsList, setConnectedHotelsList] = useState<ConnectedHotelRow[]>([]);
  const [loadingConnectedHotels, setLoadingConnectedHotels] = useState(false);
  const [connectedHotelsError, setConnectedHotelsError] = useState('');

  useEffect(() => {
    loadAccounts();
    loadInvitations();
  }, []);

  useEffect(() => {
    if (initialSearch === undefined || initialSearch === null) return;
    setSearchTerm(initialSearch);
    onInitialSearchConsumed?.();
  }, [initialSearch, onInitialSearchConsumed]);

  const hotelChipsFor = (saasId: string): RelationChip[] =>
    (graph.hotelsBySaasId.get(saasId) ?? []).map(hotel => ({
      key: hotel.id,
      label: hotel.name,
      hint: hotel.code,
      status: hotel.status,
    }));

  const loadInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const { data, error: queryError } = await supabase
        .from('saas_invitations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (queryError) throw queryError;

      setInvitations(data || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const generateInvitation = async () => {
    try {
      setGeneratingInvitation(true);
      setError('');

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('You must be logged in to generate invitations');
      }

      // Create invitation
      const { data, error: insertError } = await supabase
        .from('saas_invitations')
        .insert([{
          created_by: user.id,
          invited_company_name: invitedCompanyName.trim() || null,
        }])
        .select('token')
        .single();

      if (insertError) throw insertError;

      if (!data || !data.token) {
        throw new Error('Failed to generate invitation token');
      }

      // Generate the full invitation URL
      const invitationUrl = `${window.location.origin}/onboarding/saas?token=${data.token}`;
      setGeneratedInvitationUrl(invitationUrl);
      
      // Reset company name field
      setInvitedCompanyName('');
      
      // Reload invitations list
      loadInvitations();
    } catch (err: any) {
      console.error('Error generating invitation:', err);
      setError(err.message || 'Failed to generate invitation link');
    } finally {
      setGeneratingInvitation(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedInvitationUrl) return;

    try {
      await navigator.clipboard.writeText(generatedInvitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const closeInvitationModal = () => {
    setShowInvitationModal(false);
    setGeneratedInvitationUrl(null);
    setCopied(false);
    setError('');
    setInvitedCompanyName('');
  };

  const getInvitationStatus = (invitation: SaasInvitation): { label: string; color: string } => {
    if (invitation.used_at) {
      return { label: 'Used', color: 'bg-gray-100 text-gray-800' };
    }
    const expiresAt = new Date(invitation.expires_at);
    if (expiresAt < new Date()) {
      return { label: 'Expired', color: 'bg-red-100 text-red-800' };
    }
    return { label: 'Active', color: 'bg-green-100 text-green-800' };
  };

  const copyInvitationLink = async (invitation: SaasInvitation) => {
    const invitationUrl = `${window.location.origin}/onboarding/saas?token=${invitation.token}`;
    
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopiedInvitationId(invitation.id);
      setTimeout(() => setCopiedInvitationId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy invitation link to clipboard');
    }
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeletingAccount(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-delete-account', {
        body: { userId: accountToDelete.id, userType: 'saas' },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setAccountToDelete(null);
      await loadAccounts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      setError(message);
      console.error('Delete SaaS account error:', err);
    } finally {
      setDeletingAccount(false);
    }
  };

  const openScopesModal = async (account: SaasAccount) => {
    setAccountScopesModal(account);
    setScopesModalError('');
    setLoadingScopes(true);
    try {
      const { data: scopesData, error: scopesError } = await supabase
        .from('pms_scopes')
        .select('id, name')
        .order('name');

      if (scopesError) throw scopesError;

      const transformed = (scopesData || []).map((s: { id: string; name: string }) => ({
        code: s.name.toLowerCase().replace(/\s+/g, '_'),
        name: s.name,
      }));
      setAvailableScopes(transformed);

      const { data: saasDetails, error: saasErr } = await supabase
        .from('saas_details')
        .select('scopes')
        .eq('id', account.id)
        .single();

      if (saasErr) throw saasErr;

      const current = (saasDetails?.scopes as string[] | null) || [];
      const validSelected = current.filter((code: string) =>
        transformed.some((t) => t.code === code)
      );
      setSelectedScopeCodes(validSelected);
    } catch (err) {
      console.error('Error loading scopes for edit:', err);
      setScopesModalError(err instanceof Error ? err.message : 'Failed to load scopes');
    } finally {
      setLoadingScopes(false);
    }
  };

  const closeScopesModal = () => {
    setAccountScopesModal(null);
    setAvailableScopes([]);
    setSelectedScopeCodes([]);
    setScopesModalError('');
  };

  const handleScopeToggle = (code: string) => {
    setSelectedScopeCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSaveScopes = async () => {
    if (!accountScopesModal) return;
    setSavingScopes(true);
    setScopesModalError('');
    try {
      const { error: rpcError } = await supabase.rpc('update_saas_scopes_by_internal', {
        p_saas_id: accountScopesModal.id,
        scopes_array: selectedScopeCodes,
      });
      if (rpcError) throw rpcError;
      closeScopesModal();
    } catch (err) {
      setScopesModalError(err instanceof Error ? err.message : 'Failed to update scopes');
      console.error('Update SaaS scopes error:', err);
    } finally {
      setSavingScopes(false);
    }
  };

  const openAddHotelModal = async (account: SaasAccount) => {
    setAccountAddHotelModal(account);
    setAddHotelError('');
    setAddHotelSelectedHotelId(null);
    setAddHotelSearchTerm('');
    setAddHotelLoading(true);
    try {
      const [hotelsRes, integrationsRes, saasDetailsRes, scopesRes] = await Promise.all([
        supabase.from('hotel_accounts_secure').select('id, company_name, email, city, country').order('company_name'),
        supabase.from('integrations').select('hotel_id').eq('saas_id', account.id),
        supabase.from('saas_details').select('scopes').eq('id', account.id).single(),
        supabase.from('pms_scopes').select('id, name').order('name'),
      ]);

      if (hotelsRes.error) throw hotelsRes.error;
      if (integrationsRes.error) throw integrationsRes.error;
      if (saasDetailsRes.error) throw saasDetailsRes.error;
      if (scopesRes.error) throw scopesRes.error;

      const connectedIds = new Set((integrationsRes.data || []).map((r: { hotel_id: string }) => r.hotel_id));
      const allHotels = (hotelsRes.data || []) as HotelOption[];
      setAddHotelHotels(allHotels.filter((h) => !connectedIds.has(h.id)));
      setAddHotelConnectedIds(connectedIds);

      const saasScopes = (saasDetailsRes.data?.scopes as string[] | null) || [];
      const scopeList = (scopesRes.data || []).map((s: { id: string; name: string }) => ({
        code: s.name.toLowerCase().replace(/\s+/g, '_'),
        name: s.name,
      }));
      setAddHotelAvailableScopes(scopeList);
      const validDefault = saasScopes.filter((code: string) => scopeList.some((t) => t.code === code));
      setAddHotelScopes(validDefault.length ? validDefault : saasScopes);
    } catch (err) {
      console.error('Error loading Add hotel data:', err);
      setAddHotelError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setAddHotelLoading(false);
    }
  };

  const closeAddHotelModal = () => {
    setAccountAddHotelModal(null);
    setAddHotelHotels([]);
    setAddHotelConnectedIds(new Set());
    setAddHotelSelectedHotelId(null);
    setAddHotelScopes([]);
    setAddHotelAvailableScopes([]);
    setAddHotelError('');
    setAddHotelSearchTerm('');
  };

  const handleAddHotelScopeToggle = (code: string) => {
    setAddHotelScopes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleCreateIntegration = async () => {
    if (!accountAddHotelModal || !addHotelSelectedHotelId) return;
    setAddHotelSaving(true);
    setAddHotelError('');
    try {
      const { error: rpcError } = await supabase.rpc('create_integration_by_internal', {
        p_saas_id: accountAddHotelModal.id,
        p_hotel_id: addHotelSelectedHotelId,
        p_scopes: addHotelScopes,
        p_status: 'active',
      });
      if (rpcError) throw rpcError;
      closeAddHotelModal();
      await loadAccounts();
    } catch (err) {
      setAddHotelError(err instanceof Error ? err.message : 'Failed to create integration');
      console.error('Create integration error:', err);
    } finally {
      setAddHotelSaving(false);
    }
  };

  const openConnectedHotelsModal = async (account: SaasAccount) => {
    setAccountConnectedHotelsModal(account);
    setConnectedHotelsError('');
    setLoadingConnectedHotels(true);
    try {
      const { data: integrationsData, error: intError } = await supabase
        .from('integrations')
        .select('id, hotel_id, scopes, status')
        .eq('saas_id', account.id)
        .order('hotel_id');

      if (intError) throw intError;

      const integrations = integrationsData || [];
      if (integrations.length === 0) {
        setConnectedHotelsList([]);
        setLoadingConnectedHotels(false);
        return;
      }

      const hotelIds = [...new Set(integrations.map((i: { hotel_id: string }) => i.hotel_id))];

      type DetailRow = { company_name: string | null; hotel_code?: string | null };
      let detailsById = new Map<string, DetailRow>();
      let emailById = new Map<string, string>();

      const profilesRes = await supabase.from('profiles').select('id, email').in('id', hotelIds);
      if (!profilesRes.error) {
        emailById = new Map(
          (profilesRes.data || []).map((p: { id: string; email: string | null }) => [p.id, p.email ?? ''])
        );
      }

      // Try company_name first (post–pink_snow schema); fallback to hotel_name if column missing
      let nameError: string | null = null;
      const hotelDetailsRes = await supabase.from('hotel_details').select('id, company_name').in('id', hotelIds);
      if (hotelDetailsRes.error && hotelDetailsRes.error.message?.includes('company_name')) {
        const fallback = await supabase.from('hotel_details').select('id, hotel_name').in('id', hotelIds);
        if (!fallback.error && fallback.data) {
          detailsById = new Map(
            (fallback.data as { id: string; hotel_name: string | null }[]).map((h) => [
              h.id,
              { company_name: h.hotel_name ?? null },
            ])
          );
        } else {
          nameError = hotelDetailsRes.error?.message ?? null;
        }
      } else if (!hotelDetailsRes.error && hotelDetailsRes.data) {
        detailsById = new Map(
          (hotelDetailsRes.data as { id: string; company_name: string | null }[]).map((h) => [
            h.id,
            { company_name: h.company_name ?? null },
          ])
        );
      } else if (hotelDetailsRes.error) {
        nameError = hotelDetailsRes.error.message;
      }
      // Fetch hotel_code separately so missing column does not break the whole load
      const codeRes = await supabase.from('hotel_details').select('id, hotel_code').in('id', hotelIds);
      if (!codeRes.error && codeRes.data) {
        const codeMap = new Map((codeRes.data as { id: string; hotel_code: string | null }[]).map((h) => [h.id, h.hotel_code ?? null]));
        detailsById = new Map([...detailsById.entries()].map(([id, d]) => [id, { ...d, hotel_code: codeMap.get(id) ?? null }]));
      }

      const rows: ConnectedHotelRow[] = integrations.map((i: { id: string; hotel_id: string; scopes: string[] | null; status: string }) => {
        const details = detailsById.get(i.hotel_id);
        const email = emailById.get(i.hotel_id) ?? '';
        return {
          integration_id: i.id,
          hotel_id: i.hotel_id,
          company_name: details?.company_name ?? null,
          hotel_code: details?.hotel_code ?? null,
          email,
          scopes: i.scopes ?? [],
          status: i.status,
        };
      });
      setConnectedHotelsList(rows);
      if (nameError && !detailsById.size) {
        setConnectedHotelsError(
          `Hotel names/emails may be missing (${nameError}). Apply migration 20260209000003_internal_view_hotel_details_profiles to fix.`
        );
      } else if (profilesRes.error) {
        setConnectedHotelsError(profilesRes.error.message || 'Could not load some emails.');
      } else {
        setConnectedHotelsError('');
      }
    } catch (err) {
      console.error('Error loading connected hotels:', err);
      const message = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: string }).message)
        : err instanceof Error
          ? err.message
          : 'Failed to load connected hotels';
      setConnectedHotelsError(message);
    } finally {
      setLoadingConnectedHotels(false);
    }
  };

  const closeConnectedHotelsModal = () => {
    setAccountConnectedHotelsModal(null);
    setConnectedHotelsList([]);
    setConnectedHotelsError('');
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      setDeletingInvitationId(invitationId);
      setError('');

      const { error: deleteError } = await supabase
        .from('saas_invitations')
        .delete()
        .eq('id', invitationId);

      if (deleteError) throw deleteError;

      // Remove from local state
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      setShowDeleteConfirm(null);
    } catch (err: any) {
      console.error('Error deleting invitation:', err);
      setError(err.message || 'Failed to delete invitation');
    } finally {
      setDeletingInvitationId(null);
    }
  };

  const loadAccounts = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('saas_accounts_secure')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      setAccounts(data || []);
    } catch (err) {
      setError('Failed to load SaaS accounts');
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => 
    account.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Loading SaaS accounts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Layout className="h-6 w-6 text-indigo-600 mr-2" />
          <h2 className="text-xl font-bold text-gray-900">SaaS Accounts</h2>
        </div>
        <button
          onClick={() => setShowInvitationModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Generate Invitation Link
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 text-sm rounded-md">
          {error}
        </div>
      )}

      <div className="mb-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by company name or email"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 pl-10"
              />
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Connected Hotels
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAccounts.map((account) => (
              <tr key={account.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{account.company_name}</div>
                  <div className="text-sm text-gray-500">
                    <a href={account.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">
                      {account.website}
                    </a>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{account.contact_email}</div>
                  <div className="text-sm text-gray-500">{account.contact_phone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.city}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    account.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {account.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <RelationChips
                    chips={hotelChipsFor(account.id)}
                    onSelect={onOpenHotel ? chip => onOpenHotel(chip.label) : undefined}
                    emptyLabel={relationEmptyLabel('No hotel connected')}
                    emptyVariant={relationEmptyVariant}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openConnectedHotelsModal(account)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                      title="Connected hotels"
                    >
                      <Building2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openAddHotelModal(account)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      title="Add hotel"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openScopesModal(account)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      title="Edit scopes"
                    >
                      <Layers className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountToDelete(account)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                      title="Delete account"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAccounts.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {searchTerm
                ? 'No accounts found matching your search criteria.'
                : 'No SaaS accounts found.'}
            </p>
          </div>
        )}
      </div>

      {accountScopesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="edit-scopes-title">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 id="edit-scopes-title" className="text-lg font-semibold text-gray-900">
                Edit scopes – {accountScopesModal.company_name}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{accountScopesModal.email}</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {scopesModalError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {scopesModalError}
                </div>
              )}
              {loadingScopes ? (
                <div className="text-sm text-gray-500">Loading scopes...</div>
              ) : (
                <div className="space-y-2">
                  {availableScopes.map((scope) => (
                    <label
                      key={scope.code}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopeCodes.includes(scope.code)}
                        onChange={() => handleScopeToggle(scope.code)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-900">{scope.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeScopesModal}
                disabled={savingScopes}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveScopes}
                disabled={savingScopes || loadingScopes}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {savingScopes ? 'Saving...' : 'Save scopes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {accountConnectedHotelsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="connected-hotels-title">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 id="connected-hotels-title" className="text-lg font-semibold text-gray-900">
                  Connected hotels – {accountConnectedHotelsModal.company_name}
                </h3>
                <p className="mt-1 text-sm text-gray-500">{accountConnectedHotelsModal.email}</p>
              </div>
              <button
                type="button"
                onClick={closeConnectedHotelsModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {connectedHotelsError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {connectedHotelsError}
                </div>
              )}
              {loadingConnectedHotels ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : connectedHotelsList.length === 0 ? (
                <p className="text-sm text-gray-500">No hotels connected yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hotel
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hotel code
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Integration scopes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {connectedHotelsList.map((row) => (
                        <tr key={row.integration_id}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {row.company_name ?? row.email ?? row.hotel_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                            {row.hotel_code ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {row.email}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              row.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : row.status === 'inactive'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-amber-100 text-amber-800'
                            }`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {row.scopes.length === 0 ? (
                              <span className="text-sm text-gray-400">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {row.scopes.map((code) => (
                                  <span
                                    key={code}
                                    className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-indigo-50 text-indigo-800"
                                  >
                                    {code}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {accountAddHotelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="add-hotel-title">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 id="add-hotel-title" className="text-lg font-semibold text-gray-900">
                Add hotel – {accountAddHotelModal.company_name}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{accountAddHotelModal.email}</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {addHotelError && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {addHotelError}
                </div>
              )}
              {addHotelLoading ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel</label>
                    <input
                      type="text"
                      value={addHotelSearchTerm}
                      onChange={(e) => setAddHotelSearchTerm(e.target.value)}
                      placeholder="Search by name or email"
                      className="w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    />
                    <div className="mt-2 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                      {addHotelHotels
                        .filter(
                          (h) =>
                            !addHotelSearchTerm ||
                            (h.company_name ?? '')
                              .toLowerCase()
                              .includes(addHotelSearchTerm.toLowerCase()) ||
                            (h.email ?? '')
                              .toLowerCase()
                              .includes(addHotelSearchTerm.toLowerCase())
                        )
                        .map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => setAddHotelSelectedHotelId(addHotelSelectedHotelId === h.id ? null : h.id)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                              addHotelSelectedHotelId === h.id ? 'bg-indigo-50 text-indigo-800' : 'text-gray-900'
                            }`}
                          >
                            <span className="font-medium">{h.company_name ?? h.email ?? h.id}</span>
                            {h.email && <span className="text-gray-500 ml-2">{h.email}</span>}
                          </button>
                        ))}
                      {addHotelHotels.filter(
                        (h) =>
                          !addHotelSearchTerm ||
                          (h.company_name ?? '').toLowerCase().includes(addHotelSearchTerm.toLowerCase()) ||
                          (h.email ?? '').toLowerCase().includes(addHotelSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-4 text-sm text-gray-500">
                          {addHotelHotels.length === 0
                            ? 'No hotels available to add (all are already connected).'
                            : 'No matches for your search.'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Scopes (default: SaaS scopes)</label>
                    <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {addHotelAvailableScopes.map((scope) => (
                        <label
                          key={scope.code}
                          className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={addHotelScopes.includes(scope.code)}
                            onChange={() => handleAddHotelScopeToggle(scope.code)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-900">{scope.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeAddHotelModal}
                disabled={addHotelSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateIntegration}
                disabled={addHotelSaving || addHotelLoading || !addHotelSelectedHotelId}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addHotelSaving ? 'Adding...' : 'Add hotel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {accountToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="delete-saas-title">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 id="delete-saas-title" className="text-lg font-semibold text-gray-900">Delete SaaS account?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Delete the account for <strong>{accountToDelete.company_name}</strong> ({accountToDelete.email})? This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setAccountToDelete(null)}
                disabled={deletingAccount}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deletingAccount ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Generate SaaS Invitation</h3>
              <button
                onClick={closeInvitationModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
                {error}
              </div>
            )}

            {!generatedInvitationUrl ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Generate a unique invitation link that allows a third party to sign up for a SaaS account. 
                  The link will expire in 30 days and can only be used once.
                </p>
                <div className="mb-4">
                  <label htmlFor="invitedCompanyName" className="block text-sm font-medium text-gray-700 mb-1">
                    Invited SaaS (optional)
                  </label>
                  <input
                    type="text"
                    id="invitedCompanyName"
                    value={invitedCompanyName}
                    onChange={(e) => setInvitedCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: Enter the name of the company you're inviting for reference
                  </p>
                </div>
                <button
                  onClick={generateInvitation}
                  disabled={generatingInvitation}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingInvitation ? 'Generating...' : 'Generate Invitation Link'}
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Invitation link generated successfully! Share this link with the third party:
                </p>
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <code className="text-xs text-gray-800 break-all flex-1 mr-2">
                      {generatedInvitationUrl}
                    </code>
                    <button
                      onClick={copyToClipboard}
                      className="ml-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={closeInvitationModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setGeneratedInvitationUrl(null);
                      setError('');
                      setInvitedCompanyName('');
                    }}
                    className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Generate Another
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Invitations Section */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Invitations</h3>
        {loadingInvitations ? (
          <div className="text-center py-4">
            <div className="text-gray-500">Loading invitations...</div>
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">No invitations generated yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited SaaS
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Used Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => {
                  const status = getInvitationStatus(invitation);
                  const isCopied = copiedInvitationId === invitation.id;
                  const isDeleting = deletingInvitationId === invitation.id;
                  const showConfirm = showDeleteConfirm === invitation.id;
                  
                  return (
                    <tr key={invitation.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {invitation.invited_company_name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invitation.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invitation.expires_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {invitation.used_email || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {invitation.used_at ? new Date(invitation.used_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => copyInvitationLink(invitation)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                            title="Copy invitation link"
                          >
                            {isCopied ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                          {!showConfirm ? (
                            <button
                              onClick={() => setShowDeleteConfirm(invitation.id)}
                              disabled={isDeleting}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete invitation"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleDeleteInvitation(invitation.id)}
                                disabled={isDeleting}
                                className="px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                              >
                                {isDeleting ? 'Deleting...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm(null)}
                                disabled={isDeleting}
                                className="px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}